import { useState, useEffect } from 'react';
import { getQuotesByJob, getQuote, type Quote, type QuoteDetail } from '@/lib/api/quotes';
import { createInvoiceFromQuote } from '@/lib/api/invoices';
import { useToast } from '@/lib/hooks/useToast';

interface ImportFromQuoteModalProps {
  isOpen: boolean;
  jobId: string;
  clientId?: number;
  onClose: () => void;
  onSuccess: () => void;
}

export const ImportFromQuoteModal = ({
  isOpen,
  jobId,
  onClose,
  onSuccess,
}: ImportFromQuoteModalProps) => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [selectedQuoteDetail, setSelectedQuoteDetail] = useState<QuoteDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      loadQuotes();
    }
  }, [isOpen]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const data = await getQuotesByJob(jobId);
      // Filter for approved quotes only
      const approvedQuotes = data.filter(q => q.status?.toLowerCase() === 'approved');
      setQuotes(approvedQuotes);
      
      if (approvedQuotes.length === 0) {
        toast.warning('No approved quotes found for this job');
      }
    } catch (error) {
      console.error('Failed to load quotes:', error);
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectQuote = async (quoteId: string) => {
    try {
      setSelectedQuoteId(quoteId);
      const detail = await getQuote(quoteId);
      setSelectedQuoteDetail(detail);
    } catch (error) {
      console.error('Failed to load quote details:', error);
      toast.error('Failed to load quote details');
      setSelectedQuoteId(null);
      setSelectedQuoteDetail(null);
    }
  };

  const handleImport = async () => {
    if (!selectedQuoteId) return;

    try {
      setCreating(true);
      const result = await createInvoiceFromQuote(parseInt(selectedQuoteId));
      toast.success(`Invoice ${result.invoiceNumber} created from quote`);
      onClose();
      onSuccess();
    } catch (error) {
      console.error('Failed to import quote:', error);
      toast.error('Failed to import quote as invoice');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Import from Quote</h3>

        {loading ? (
          <div className="flex justify-center py-8">
            <span className="material-symbols-outlined animate-spin text-2xl text-gray-400">
              progress_activity
            </span>
          </div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-8">
            <span className="material-symbols-outlined text-4xl text-gray-300 mb-3 block">
              description
            </span>
            <p className="text-gray-600">No approved quotes available for this job</p>
          </div>
        ) : (
          <>
            <div className="space-y-2 mb-6 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {quotes.map(quote => (
                <button
                  key={quote.id}
                  onClick={() => handleSelectQuote(quote.id)}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                    selectedQuoteId === quote.id
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{quote.quote_number}</p>
                      <p className="text-xs text-gray-500 mt-1">{quote.client_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">${quote.total.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {selectedQuoteDetail && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                <p className="text-sm font-semibold text-gray-900 mb-3">Quote Details</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">${selectedQuoteDetail.subtotal?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax:</span>
                    <span className="font-medium">${selectedQuoteDetail.tax_amount?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-300 pt-2">
                    <span className="text-gray-900 font-semibold">Total:</span>
                    <span className="font-bold text-primary">${selectedQuoteDetail.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!selectedQuoteDetail || creating}
                className="flex-1 px-4 py-2 text-sm font-bold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                    Importing...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-sm">file_copy</span>
                    Create Invoice
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
